import { Events, Log, Result, SessionProvider } from 'core/infrastructure';
import { BridgeProxy, Electron } from 'pos/common';
import { AuditFacade, CashdrawerFacade, PrintFacade } from 'pos/facades';
import { Permission, Res } from 'core/api';
import { CashControlAction } from 'pos/common/BridgeProxy.Endpoints';

export type TransactResult = Omit<Exclude<typeof BridgeProxy.Endpoints.CashControl.Transaction.response, undefined>, 'ok' | 'pendingActions'>;

async function redrawAfter<T>(func: () => Promise<T>): Promise<T> {
	try {
		return await func();
	}
	finally {
		// ÑAPA: CashlogyConnector a veces roba el foco y luego electron no pinta bien las cosas y se queda
		// tostado hasta que se hace un click de ratón (real, no sirve un touch)
		await Electron.Window.forceRedraw();
	}
}

export async function transact(amountToPay: number): Promise<Result<TransactResult>> {
	return redrawAfter(async () => {
		assertUseCashControl();

		// Aseguro que están todas las acciones previas procesadas, porque si hubiera un FixPosAmount y nos lo devolviera
		// en el result, se procesaría el FixPosAmount *antes* de completar el cierre del ticket. Eso haría que se generasen
		// dos movimientos en el TPV:
		// - Uno por el FixPosAmount, ya que en ese momento Ágora todavía piensa que le falta el dinero del ticket porque
		//   aún no se ha cerrado.
		// - Otro cuando realmente se cierra el ticket.
		// Eso haría que la caja se descuadrase.
		await processPendingActions();

		const result = await BridgeProxy.invoke('REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.CashControl.Transaction, { amountToPay });

		if (!result.ok)
			return Result.error();

		await processActions(result.pendingActions);

		return Result.ok({
			amountToPay: result.amountToPay,
			change: result.change,
			paid: result.paid
		});
	});
}

async function processActions(actions: CashControlAction[]): Promise<void> {

	const nonFixAmountActions = actions.filter(action => action.type !== 'FixPosAmount').sortBy(x => x.date);
	const fixPosAmountAction = actions.filter(action => action.type === 'FixPosAmount').last();
	const actionsToProcess = fixPosAmountAction ? [...nonFixAmountActions, fixPosAmountAction] : nonFixAmountActions;

	for (const action of actionsToProcess) {
		try {
			switch (action.type) {
				case 'Audit':
					await AuditFacade.audit(action.eventType, action.action, action.date);
					break;
				case 'FixPosAmount': {
					const posAmount = await CashdrawerFacade.getCurrentAmount();
					const cashControlAmountResult = await getCurrentAmount();
					if (cashControlAmountResult.ok) {
						const cashControlOverPos = cashControlAmountResult.value - posAmount;
						if (cashControlOverPos === 0)
							break;

						// Si cashControl tiene más que el Pos, tengo que registrar una operación de entrada para igualar el pos al cashcontrol
						if (cashControlOverPos > 0)
							await CashdrawerFacade.registerInputOperation(Res.Msg.MSG_CASH_CONTROL_PROVIDER(), Res.Msg.MSG_CASH_AMOUNT_CHANGES(), cashControlOverPos, false, true);
						// Si no, tengo que registrar una salida del pos para igualarlo al cashcontrol
						else
							await CashdrawerFacade.registerOutputOperation(Res.Msg.MSG_CASH_CONTROL_PROVIDER(), Res.Msg.MSG_CASH_AMOUNT_CHANGES(), Math.abs(cashControlOverPos) /* Nuestro API siempre recibe el output en positivo */, false, true);
					}
					break;
				}
				case 'InputOperation':
					await CashdrawerFacade.registerInputOperation(action.provider, action.description, action.amount, action.printReceipt, true, action.date);
					break;
				case 'OutputOperation':
					await CashdrawerFacade.registerOutputOperation(action.provider, action.description, action.amount, action.printReceipt, true, action.date);
					break;
				case 'HideNotification':
					await Events.raise('pos:cash-control:hide-notification');
					break;
				case 'ShowNotification':
					await Events.raise('pos:cash-control:show-notification');
					break;
				case 'PrintCloseDayResult':
					await PrintFacade.printCashControlStatus(action.coinsValues, action.notesValues, action.remainingChange);
					break;
			}
		} catch (error: any) {
			Log.warn(`No se pudo procesar la petición ${action.type}: ${error.message}`);
		}
	}
}

export function startListenPendingActions(): void {
	setInterval(processPendingActions, 60 * 1000);
}

async function processPendingActions(): Promise<void> {
	const result = await BridgeProxy.invoke('NO_REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.CashControl.PendingActions);
	await processActions(result.pendingActions);
}

function assertUseCashControl() {
	const session = SessionProvider.getCurrent();
	if (!session.UseCashControl)
		throw new Error('No se puede utilizar un CashControl si no hay máquina de control de efectivo configurada');
}

export async function getCurrentAmount(): Promise<Result<number>> {
	// Tengo que registrar actividad de usuario porque Cashguard tarda mucho (hasta 20s) en contestar, y eso puede hacer
	// que se lance un cierre de sesión mientras se está cerrando jornada
	const result = await BridgeProxy.invoke('REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.CashControl.CurrentAmount);

	if (result.success)
		return Result.ok(result.amount);

	return Result.error();
}

export async function showOperations(): Promise<void> {
	return redrawAfter(async () => {

		assertUseCashControl();

		// Aseguro que están todas las acciones previas procesadas, porque si hubiera un FixPosAmount y nos lo devolviera
		// en el result, si meten cambio u cambian la cantidad, aparecerían dos variaciones de efectivo. La que metemos en el servidor
		// y la que meterías del FixPosAmount.
		await processPendingActions();

		const result = await BridgeProxy.invoke(
			'REGISTER_USER_ACTIVITY',
			BridgeProxy.Endpoints.CashControl.Operations,
			{
				canAddChange: SessionProvider.hasPermission(Permission.Cashdrawer_RegisterInputCashTransaction),
				canReset: SessionProvider.hasPermission(Permission.CashControl_Reset),
				canShowAdvancedOptions: SessionProvider.hasPermission(Permission.Pos_CreateOrModify),
				canShowStatus: SessionProvider.hasPermission(Permission.CashControl_CashControlOperations)
			}
		);

		await processActions(result.pendingActions);
	});
}


export async function giveChange(): Promise<void> {
	return redrawAfter(async () => {
		assertUseCashControl();

		// Aseguro que están todas las acciones previas procesadas, porque si hubiera un FixPosAmount y nos lo devolviera
		// en el result, si meten cambio u cambian la cantidad, aparecerían dos variaciones de efectivo. La que metemos en el servidor
		// y la que meterías del FixPosAmount.
		await processPendingActions();

		const result = await BridgeProxy.invoke('REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.CashControl.GiveChange);
		await processActions(result.pendingActions);
	});
}

export async function closeDay(): Promise<void> {
	return redrawAfter(async () => {

		assertUseCashControl();

		// Aseguro que están todas las acciones previas procesadas, porque si hubiera un FixPosAmount y nos lo devolviera
		// en el result, si realizan alguna operación que queda registrada en el servidor, aparecerían dos variaciones de efectivo.
		await processPendingActions();

		const result = await BridgeProxy.invoke(
			'REGISTER_USER_ACTIVITY',
			BridgeProxy.Endpoints.CashControl.CloseDay,
			{ canOpenCashDrawers: SessionProvider.hasPermission(Permission.Cashdrawer_RegisterOutputCashTransaction) }
		);

		await processActions(result.pendingActions);
	});
}